import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, BellOff, BellRing, ChevronRight, Clock3, Compass, Gauge, Sigma,
} from "lucide-react";
import { isNum, num, pts, rupees } from "./fmt";
import { marketTile, movementTile, regimeLines, rulesTile } from "./AtAGlance";
import { simpleAction, simpleRegime } from "./SimpleSignal";
import { combinedVerdict, mergedReasons, nextStep, OrderTicket, payState, RulesRow, WhyNot } from "./VerdictCard";

/*
 * BUYER FUNNEL — the whole decision as four gates and one answer (2026-10-06).
 *
 * An option buyer only profits when the index moves far enough, soon enough,
 * to pay the premium, the costs and the time decay. So the gates are read in
 * the order the research earned them:
 *
 *   1  Market open?          session phase, minutes left for new entries
 *   2  Moving enough?        the atr_pct gate every live rule uses (+ day regime)
 *   3  Can the move pay?     expected move vs the cheaper side's breakeven/target
 *   4  Which side?           trend regime (context: direction is ~50-55% right)
 *   =  BUY CALL / BUY PUT / NO TRADE, one sentence of why, and either the
 *      order ticket or the one thing that would change the answer.
 *
 * Pure presentation over payloads the Dashboard already holds.
 */

export const TONE = {
  emerald: { shell: "border-emerald-500/45 bg-emerald-500/[.07]", ink: "text-emerald-300", bar: "bg-emerald-400" },
  rose: { shell: "border-rose-500/45 bg-rose-500/[.07]", ink: "text-rose-300", bar: "bg-rose-400" },
  amber: { shell: "border-amber-500/40 bg-amber-500/[.06]", ink: "text-amber-300", bar: "bg-amber-400" },
  cyan: { shell: "border-cyan-500/40 bg-cyan-500/[.06]", ink: "text-cyan-300", bar: "bg-cyan-400" },
  slate: { shell: "border-line bg-surface-2", ink: "text-slate-300", bar: "bg-slate-500" },
};
const tone = (t) => TONE[t] || TONE.slate;

/* Step 3 — can the expected move pay for the cheaper side after costs?
 * Read from the side blocks (can_breakeven / can_profit), NOT from
 * verdict.edge, which is also false for a kill switch, the opening minutes,
 * the cutoff or a missing chain. */
export function payTile(edge, view) {
  if (view?.sessionClosed) return { answer: "—", tone: "slate", detail: "Measured only while the market is open." };
  if (!edge) return { answer: "Loading", tone: "slate", detail: "Waiting for the buyer-edge feed." };
  const state = payState(edge);
  const em = edge.expected_move?.blend_rest;
  const sides = [edge.call, edge.put].filter((s) => s?.available);
  const be = sides.map((s) => s.breakeven_spot_move).filter(isNum).map(Number);
  const need = sides.map((s) => s.required_spot_move).filter(isNum).map(Number);
  const cheapBe = be.length ? Math.min(...be) : null;
  const cheapNeed = need.length ? Math.min(...need) : null;
  const detail = isNum(em) && Number(em) > 0
    ? `Expected move ±${num(em, 0)} pts left today.${cheapBe != null ? ` Cheaper side needs ${pts(cheapBe)} to cover costs` : ""}${cheapNeed != null ? `, ${pts(cheapNeed)} for the target.` : "."}`
    : "No expected move yet — needs the live chain and 5-minute candles.";
  const theta = [edge.call, edge.put].filter((s) => s?.available).map((s) => s.theta_rupees_per_hour_per_lot).filter(isNum).map(Number);
  const thetaLine = theta.length ? ` Waiting costs about ₹${num(Math.min(...theta), 0)} an hour per lot.` : "";
  const base = { detail: detail + thetaLine, em: isNum(em) ? Number(em) : null, cheapBe, cheapNeed, thetaPerHour: theta.length ? Math.min(...theta) : null };
  if (state === "profit") return { answer: "Yes", tone: "emerald", ...base };
  if (state === "breakeven") return { answer: "Only costs", tone: "amber", ...base, detail: `${detail} The move covers charges but not the usual target.${thetaLine}` };
  if (state === "none") return { answer: "No", tone: "rose", ...base };
  return { answer: "Unknown", tone: "amber", ...base, detail: "No live option price yet — needs the chain." };
}

/* The four gates as plain objects (tested). */
export function funnelSteps({ view, edge, trend, regime } = {}) {
  const m = marketTile(view, edge?.session);
  const mv = movementTile(edge, view);
  const pay = payTile(edge, view);
  const r = simpleRegime(trend, view);
  return [
    { key: "session", n: 1, question: "Is the market open?", icon: Clock3, answer: m.answer, tone: m.tone, detail: m.detail },
    { key: "move", n: 2, question: "Is it moving enough?", icon: Gauge, answer: mv.answer, tone: mv.tone, detail: mv.detail, ratio: mv.ratio, regime: regimeLines(regime, view) },
    { key: "pay", n: 3, question: "Can the move pay?", icon: Sigma, answer: pay.answer, tone: pay.tone, detail: pay.detail, pay },
    { key: "side", n: 4, question: "Which side?", icon: r.Icon || Compass, answer: r.word, tone: r.tone, detail: r.detail },
  ];
}

/* ── pieces (GateStep is shared with the Manual Trader's pre-trade check) ── */

export function GateStep({ n, question, answer, tone: t = "slate", detail, icon: Icon, children, last = false, testId }) {
  const c = tone(t);
  return (
    <div data-testid={testId} className={`sheen relative flex min-w-0 flex-col rounded-xl border p-3 ${c.shell}`}>
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-black text-slate-200">{n}</span>
        <span>{question}</span>
      </p>
      <div className="mt-1.5 flex items-center gap-2">
        {Icon && <Icon className={`h-6 w-6 shrink-0 ${c.ink}`} strokeWidth={2.5} />}
        <p className={`truncate text-2xl font-black leading-7 tracking-tight ${c.ink}`}>{answer}</p>
      </div>
      {detail && <p className="mt-1 text-[12px] leading-4 text-slate-400">{detail}</p>}
      {children}
      {!last && (
        <span className="absolute -right-[13px] top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-surface text-slate-500 lg:flex">
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}

/* The atr gate as a gauge: the minimum sits at the middle; full = 2× minimum. */
export function MovementGauge({ ratio, tone: t }) {
  const fill = ratio == null ? 0 : Math.max(3, Math.min(100, (ratio / 2) * 100));
  return (
    <div className="mt-2" title="atr_pct (14-bar ATR ÷ price) against the rules' minimum">
      <div className="relative h-2 w-full rounded-full bg-slate-800">
        <div className={`h-2 rounded-full transition-[width] duration-700 ${tone(t).bar}`} style={{ width: `${fill}%` }} />
        <span className="absolute -top-1 w-0.5 rounded bg-slate-200" style={{ left: "50%", height: 16 }} />
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
        <span>quiet</span><span className="text-slate-300">minimum</span><span>very active</span>
      </div>
    </div>
  );
}

function RegimeChips({ lines }) {
  if (!lines) return null;
  const chip = (x, testId) => x && (
    <span key={testId} data-testid={testId} title={x.detail}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${tone(x.tone).shell} ${tone(x.tone).ink}`}>
      {x.label}{x.early ? " · early" : ""}
    </span>
  );
  const note = lines.backdrop?.detail || lines.odds?.detail;
  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1.5">{chip(lines.backdrop, "regime-backdrop")}{chip(lines.odds, "regime-odds")}</div>
      {note && <p className="mt-1 text-[12px] leading-4 text-slate-500">{note}</p>}
    </div>
  );
}

/* Expected move drawn against the cheaper side's breakeven and target. */
function PayBar({ pay }) {
  if (!pay || pay.em == null || pay.cheapNeed == null) return null;
  const max = Math.max(pay.em, pay.cheapNeed) * 1.15 || 1;
  const p = (x) => Math.max(0, Math.min(100, (x / max) * 100));
  const c = tone(pay.tone);
  return (
    <div className="mt-2" title={`expected ±${num(pay.em, 0)} pts · costs ${pts(pay.cheapBe)} · target ${pts(pay.cheapNeed)}`}>
      <div className="relative h-2 w-full rounded-full bg-slate-800">
        <div className={`h-2 rounded-full ${c.bar}`} style={{ width: `${p(pay.em)}%` }} />
        {pay.cheapBe != null && <span className="absolute -top-1 h-4 w-0.5 bg-amber-300" style={{ left: `${p(pay.cheapBe)}%` }} />}
        <span className="absolute -top-1 h-4 w-0.5 bg-emerald-300" style={{ left: `${p(pay.cheapNeed)}%` }} />
      </div>
      <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
        <span>move</span><span className="text-amber-300">costs</span><span className="text-emerald-300">target</span>
      </div>
    </div>
  );
}

/* ── the card ───────────────────────────────────────────────────────────── */

function BuyerFunnel({
  view, edge, trend, regime, rules, status, activeIndex, onTrade,
  alertsEnabled = false, onToggleAlerts,
}) {
  const steps = funnelSteps({ view, edge, trend, regime });
  const closed = Boolean(view?.sessionClosed);
  const regimeWord = steps[3].answer;
  const v = combinedVerdict(edge, view);
  const action = simpleAction(edge, view, regimeWord);
  const go = Boolean(v.go && view?.tradePlan);
  const reasons = closed ? [] : mergedReasons(edge, view);
  const step = nextStep(edge, view);
  const r = rulesTile(status, rules, activeIndex);
  const vt = tone(action.tone);
  const name = activeIndex === "BANKNIFTY" ? "BANKNIFTY" : "NIFTY";
  const Icon = action.Icon;
  const glow = go ? (action.tone === "emerald" ? "glow-emerald breathe-glow" : action.tone === "rose" ? "glow-rose breathe-glow" : "") : "";

  return (
    <section data-testid="buyer-funnel" className={`rounded-2xl border p-3 sm:p-4 ${go ? `${vt.shell} ${glow}` : "border-line bg-surface"}`}>
      {/* the four gates */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {steps.map((s, i) => (
          <GateStep key={s.key} n={s.n} question={s.question} answer={s.answer} tone={s.tone} detail={s.detail}
            icon={s.icon} last={i === steps.length - 1} testId={`gate-${s.key}`}>
            {s.key === "move" && !closed && <MovementGauge ratio={s.ratio} tone={s.tone} />}
            {s.key === "move" && <RegimeChips lines={s.regime} />}
            {s.key === "pay" && !closed && <PayBar pay={s.pay} />}
          </GateStep>
        ))}
      </div>

      {/* the answer */}
      <div className="mt-4 flex flex-col gap-4 border-t border-line pt-4 lg:flex-row lg:items-start lg:gap-6">
        <div data-testid="funnel-verdict" className="shrink-0 lg:w-80">
          <div className="flex items-start gap-3">
            {Icon && <Icon className={`mt-0.5 h-12 w-12 shrink-0 ${vt.ink}`} strokeWidth={2.5} />}
            <div className="min-w-0">
              <p className={`text-4xl font-black leading-9 tracking-tight sm:text-5xl sm:leading-[2.75rem] ${vt.ink}`}>{action.word}</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-low">{v.qualifier}</p>
            </div>
          </div>
          <p className="mt-2.5 text-base leading-6 text-slate-200">
            {action.caution && <AlertTriangle className="mr-1.5 inline h-4 w-4 text-amber-300" />}{action.why}
          </p>
          {!closed && onToggleAlerts && (
            <button type="button" onClick={onToggleAlerts}
              className={`mt-3 inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold ${alertsEnabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-line bg-surface-2 text-slate-400 hover:text-hi"}`}>
              {alertsEnabled ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              {alertsEnabled ? "Alerting when it turns to BUY" : "Alert me when it turns to BUY"}
            </button>
          )}
        </div>

        <div className="min-w-0 flex-1 border-line lg:border-l lg:pl-6">
          {go ? (
            <div className="space-y-3">
              <OrderTicket plan={view.tradePlan} underlying={name} tone={action.tone} />
              {onTrade && (
                <button type="button" data-testid="funnel-trade" onClick={() => onTrade(view.tradePlan)}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-black transition ${action.tone === "rose" ? "bg-rose-500/90 text-white hover:bg-rose-400" : action.tone === "amber" ? "bg-amber-400 text-on-dark hover:bg-amber-300" : "bg-emerald-500/90 text-on-dark hover:bg-emerald-400"}`}>
                  Place this on Manual Trader <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <WhyNot reasons={reasons} step={step} />
          )}
        </div>
      </div>

      {/* what my rules are doing — and how close the researched edge is to a trade */}
      <div className="mt-4 flex flex-wrap items-start gap-x-5 gap-y-2 border-t border-line pt-3 text-sm text-slate-300">
        <span>My rules: <b className={tone(r.tone).ink}>{r.answer}</b></span>
        {r.open.length > 0 && (
          <span>Open P&amp;L: <b className={`tabular-nums ${r.openPnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.openPnl >= 0 ? "+" : "−"}{rupees(Math.abs(r.openPnl))}</b></span>
        )}
        {r.today != null && (
          <span>Today: <b className={`tabular-nums ${r.today >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.today >= 0 ? "+" : "−"}{rupees(Math.abs(r.today))}</b></span>
        )}
        <span className="min-w-0 basis-full lg:basis-auto lg:flex-1"><RulesRow rules={rules} underlying={activeIndex} /></span>
        <Link to="/autotrader" className="text-[11px] font-bold text-cyan-400 hover:text-cyan-300">Autotrader →</Link>
      </div>
    </section>
  );
}

export default memo(BuyerFunnel);
